import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { mainSiteUrl, faqs, categories } from "@/mocks/categories";
import { articleContents, type ArticleSection } from "@/mocks/articles";
import JsonLd from "@/components/base/JsonLd";
import { usePageSeo } from "@/hooks/usePageSeo";

const siteUrl = typeof import.meta !== "undefined" && (import.meta as Record<string, unknown>).env
  ? ((import.meta as Record<string, unknown>).env as Record<string, string>).VITE_SITE_URL || "https://mulebuyspreadsheets.makeup"
  : "https://mulebuyspreadsheets.makeup";

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [articleId]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const article = articleId ? articleContents[articleId] : null;

  const articleSchema = article
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.h1,
        "description": article.seoDescription,
        "datePublished": "2026-06-22",
        "dateModified": "2026-06-22",
        "author": {
          "@type": "Organization",
          "name": "Mulebuy Spreadsheet",
        },
        "publisher": {
          "@type": "Organization",
          "name": "Mulebuy Spreadsheet",
          "url": siteUrl,
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${siteUrl}/${article.id}`,
        },
        "inLanguage": "en",
      }
    : null;

  usePageSeo({
    title: article ? article.seoTitle : "Article Not Found – Mulebuy Spreadsheet",
    description: article
      ? article.seoDescription
      : "The article you are looking for does not exist or has been moved.",
    canonicalPath: article ? `/${article.id}` : undefined,
  });

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 font-sans flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-16 h-16 flex items-center justify-center bg-stone-100 rounded-2xl mx-auto mb-4">
            <i className="ri-file-unknow-line text-2xl text-stone-400"></i>
          </div>
          <h2 className="font-heading text-2xl font-bold text-stone-800 mb-2">Article Not Found</h2>
          <p className="text-stone-500 mb-6">The article you are looking for does not exist or has been moved.</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-lg font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm"></i>
            </span>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const renderSection = (section: ArticleSection, index: number) => {
    switch (section.type) {
      case "heading":
        return (
          <h2 key={index} className="font-heading text-2xl md:text-3xl font-bold text-stone-800 mb-6 mt-12">
            {section.heading}
          </h2>
        );
      case "paragraph":
        return (
          <p key={index} className="text-stone-600 leading-relaxed mb-6">
            {section.content}
          </p>
        );
      case "image":
        return (
          <div key={index} className="my-10 rounded-2xl overflow-hidden">
            <img
              src={section.imageUrl}
              alt={section.imageAlt}
              className="w-full h-auto object-cover"
            />
          </div>
        );
      case "list":
        return (
          <div key={index} className="mb-8">
            {section.heading && (
              <h3 className="font-semibold text-stone-800 mb-3">{section.heading}</h3>
            )}
            <div className="space-y-2">
              {section.items?.map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-stone-50 rounded-xl">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className="ri-check-line text-sm text-primary-500"></i>
                  </span>
                  <span className="text-sm text-stone-600">{item}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case "table":
        return (
          <div key={index} className="overflow-x-auto rounded-xl border border-stone-200 bg-stone-50 mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-stone-100 border-b border-stone-200">
                  <th className="px-4 py-3 text-left font-semibold text-stone-700">Factor</th>
                  <th className="px-4 py-3 text-left font-semibold text-stone-700">Mulebuy Spreadsheet</th>
                  <th className="px-4 py-3 text-left font-semibold text-stone-700">Traditional / Notes</th>
                </tr>
              </thead>
              <tbody>
                {section.rows?.map((row, i) => (
                  <tr key={i} className="border-b border-stone-100">
                    <td className="px-4 py-3 font-medium text-stone-800">{row.label}</td>
                    <td className="px-4 py-3 text-stone-600">{row.col1}</td>
                    <td className="px-4 py-3 text-stone-600">{row.col2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case "steps":
        return (
          <div key={index} className="space-y-4 mb-8">
            {section.steps?.map((step) => (
              <div key={step.step} className="flex items-start gap-4 p-5 bg-stone-50 rounded-xl border border-stone-100">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-500 text-white rounded-full font-bold text-sm flex-shrink-0">
                  {step.step}
                </div>
                <div>
                  <h4 className="font-semibold text-stone-800 mb-1">{step.title}</h4>
                  <p className="text-sm text-stone-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        );
      case "faq":
        return (
          <div key={index} className="mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-stone-800 mb-6 mt-12">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-stone-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left bg-white hover:bg-stone-50 transition-colors"
                  >
                    <span className="font-semibold text-stone-800 text-sm md:text-base">{faq.question}</span>
                    <span className="w-6 h-6 flex items-center justify-center text-stone-400 flex-shrink-0 ml-3">
                      <i className={`ri-arrow-down-s-line transition-transform ${openFaq === i ? "rotate-180" : ""}`}></i>
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 text-stone-600 text-sm leading-relaxed">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      case "cta":
        return (
          <div key={index} className="bg-primary-50 rounded-2xl p-8 md:p-10 text-center mt-12">
            <h3 className="font-heading text-xl md:text-2xl font-bold text-stone-800 mb-4">
              Start Exploring Mulebuy Spreadsheet Now
            </h3>
            <p className="text-stone-600 mb-6 max-w-lg mx-auto leading-relaxed">
              Browse our complete product categories and find the best deals through our trusted China agent network.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 bg-primary-500 text-white rounded-lg font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Mulebuy Spreadsheet
              </a>
              <Link
                to="/"
                className="px-6 py-3 bg-white border-2 border-primary-500 text-primary-700 rounded-lg font-semibold text-sm hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                Back to Homepage
              </Link>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background-50 font-sans">
      {articleSchema && <JsonLd data={articleSchema as unknown as Record<string, unknown>} />}
      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white shadow-sm border-b border-stone-200/70"
            : "bg-white border-b border-stone-200/50"
        }`}
      >
        <div className="w-full px-4 md:px-8 flex items-center justify-between h-16">
          <Link to="/" className="font-heading text-xl font-bold text-stone-800 hover:text-primary-600 transition-colors whitespace-nowrap">
            Mulebuy Spreadsheet
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="hidden md:inline-flex px-3 py-2 text-sm font-semibold text-stone-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Home
            </Link>
            <Link
              to="/mulebuy-spreadsheet-shopping-guide"
              className="hidden md:inline-flex px-3 py-2 text-sm font-semibold text-stone-700 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              Shopping Guide
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Main Store
            </a>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <section className="pt-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-20">
          <div className="mb-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-primary-600 transition-colors"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm"></i>
              </span>
              Back to Home
            </Link>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-stone-800 mb-6 leading-tight">
            {article.h1}
          </h1>
          <p className="text-lg text-stone-600 leading-relaxed mb-6">
            {article.intro}
          </p>
          <div className="flex items-center gap-4 text-sm text-stone-500">
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-sm"></i>
              </span>
              {article.date}
            </span>
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-sm"></i>
              </span>
              {article.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {article.sections.map((section, i) => renderSection(section, i))}
      </article>

      {/* Internal Links */}
      <section className="bg-stone-50 py-12 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <h3 className="font-heading text-xl font-bold text-stone-800 mb-6">Explore More Mulebuy Spreadsheet Resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/"
              className="flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-200 hover:border-primary-300 hover:shadow-sm transition-all"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                <i className="ri-home-line text-lg text-primary-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-stone-800 text-sm">Homepage</h4>
                <p className="text-xs text-stone-500 mt-0.5">Browse categories and discover products</p>
              </div>
            </Link>
            <Link
              to="/mulebuy-spreadsheet-shopping-guide"
              className="flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-200 hover:border-primary-300 hover:shadow-sm transition-all"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                <i className="ri-book-open-line text-lg text-accent-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-stone-800 text-sm">Shopping Guide</h4>
                <p className="text-xs text-stone-500 mt-0.5">Complete step-by-step shopping walkthrough</p>
              </div>
            </Link>
            <Link
              to="/is-spreadsheet-shopping-safe"
              className="flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-200 hover:border-primary-300 hover:shadow-sm transition-all"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-secondary-100 rounded-lg flex-shrink-0">
                <i className="ri-shield-check-line text-lg text-secondary-600"></i>
              </div>
              <div>
                <h4 className="font-semibold text-stone-800 text-sm">Safety Guide</h4>
                <p className="text-xs text-stone-500 mt-0.5">Risk tips and agent verification advice</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-100 py-10 px-4 md:px-8 border-t border-stone-200">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-heading text-lg font-bold text-stone-800 mb-1">Mulebuy Spreadsheet</h3>
              <p className="text-sm text-stone-500">
                Your trusted guide to China product sourcing and spreadsheet shopping.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-sm text-stone-600 hover:text-primary-600 transition-colors">
                Main Store
              </a>
              <Link to="/" className="text-sm text-stone-600 hover:text-primary-600 transition-colors">
                Home
              </Link>
              <Link to="/what-is-mulebuy-spreadsheet" className="text-sm text-stone-600 hover:text-primary-600 transition-colors">
                Guide
              </Link>
              <Link to="/mulebuy-spreadsheet-shopping-guide" className="text-sm text-stone-600 hover:text-primary-600 transition-colors">
                Shopping Guide
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-stone-200 text-center">
            <p className="text-xs text-stone-400">
              &copy; 2026 Mulebuy Spreadsheet. All rights reserved. This is an informational site. All purchases are handled through third-party agents.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}